import React, { useState } from 'react';


// --- DATA CONSTANTS ---
const BEHAVIORS = {
  steadiness: [
    { id: 'patience', label: 'Patience' },
    { id: 'persistence', label: 'Persistence' },
    { id: 'thoughtfulness', label: 'Thoughtfulness' }
  ],
  influence: [
    { id: 'self-confidence', label: 'Self Confidence' },
    { id: 'enthusiasm', label: 'Enthusiasm' },
    { id: 'friendliness', label: 'Friendliness' }
  ],
  dominance: [
    { id: 'efficiency', label: 'Efficiency' },
    { id: 'independence', label: 'Independence' },
    { id: 'self-motivation', label: 'Self Motivation' }
  ],
  consc: [
    { id: 'accuracy', label: 'Accuracy' },
    { id: 'cooperativeness', label: 'Cooperativeness' },
    { id: 'sensitivity', label: 'Sensitivity' }
  ]
};


const TRAIT_CONFIG = {
  steadiness: { name: 'Steadiness', color: 'var(--color-steadiness-text)' },
  influence: { name: 'Influence', color: 'var(--color-influence-text)' },
  dominance: { name: 'Dominance', color: 'var(--color-dominance-text)' },
  consc: { name: 'Conscientiousness', color: 'var(--color-consc-text)' }
};


const PERSONALITIES = [
  { id: 'pioneer', name: 'Pioneer', desc: 'A self-reliant, ambitious, dynamic, & competitive individual. They are in constant pursuit of innovative solutions & seek new horizons to achieve goals.' },
  { id: 'influencer', name: 'Influencer', desc: 'An enthusiastic, persuasive, confident & verbally skilled individual that loves to socialize and inspire people. They have a genuine interest in contributing to creative ideas.' },
  { id: 'team-player', name: 'Team Player', desc: 'A friendly, calm, loyal & flexible individual that is remarkably consistent. They prefer practical jobs & enjoy being part of a team.' },
  { id: 'logical-thinker', name: 'Logical Thinker', desc: 'A cautious, logical, hardworking & detail-oriented individual that seeks accuracy in every activity. They combine existing rules, facts, & structures to manage work effectively.' },
  { id: 'persuader', name: 'Persuader', desc: 'An assertive, energetic & expressive individual that is capable of both direct dynamic action & charming sociability as needed. Thrives on challenges & works with determination & commitment to achieve goals.' },
  { id: 'achiever', name: 'Achiever', desc: 'A dependable, tolerant, persistent & tenacious individual that pushes past obstacles to get the job done. Can be supportive under favorable conditions & demanding if the situation calls for it.' },
  { id: 'perfectionist', name: 'Perfectionist', desc: 'A well planned, straight forward, dynamic & competitive individual that believes in getting things right & doesn\'t shy away from challenges to get things done. Prioritizes accuracy & delivers quality.' },
  { id: 'collaborator', name: 'Collaborator', desc: 'An intuitive, cooperative, loyal and social individual with a "people first" motto bringing warmth and positivity to the team. Promotes harmony and teamwork through charm and persuasion.' },
  { id: 'assessor', name: 'Assessor', desc: 'An expressive, detail-oriented, enthusiastic and persuasive individual that makes creative ideas serve practicality. They are critical thinkers and they keep pushing themselves in the quest for high-quality results.' },
  { id: 'implementor', name: 'Implementor', desc: 'A methodical, dependable, practical, and diligent individual that goes to great lengths in achieving best results. Any job they take up will be completed with keen attention to detail and accuracy.' },
  { id: 'motivator', name: 'Motivator', desc: 'An independent, self-confident, competitive and persistent individual with a high sense of responsibility. Their patience and proactivity draw people to them. Very sociable but can also be determined and assertive in demanding situations.' },
  { id: 'leader', name: 'Leader', desc: 'A self-driven, innovative, confident & dynamic individual that likes taking responsibility & has a constant drive to make things better. While they are ambitious & assertive, they are also considerate of the needs of others & bring the best out of everyone around them.' },
  { id: 'administrator', name: 'Administrator', desc: 'A practical, detail-oriented and clear-minded individual that builds harmony in the team and delivers results. Plans and organizes as per the goals and strongly pushes to achieve them.' },
  { id: 'mediator', name: 'Mediator', desc: 'A well-spoken, detail-oriented, productive, and friendly individual who is easy to collaborate with. Has the patience to find the best possible solution which is both creative and efficient.' }
];


export default function TestingUi() {
  // --- STATE ---
 
  // Gatekeeper states for optional sections (Stress & Supervisor)
  const [gatekeepers, setGatekeepers] = useState({
    stress: { enabled: false, value: '' },
    supervisor: { enabled: false, value: '' }
  });


  // Selected behaviors tracking (max 4 per section)
  const [selections, setSelections] = useState({
    primary: [],
    stress: [],
    supervisor: []
  });


  // Quick Pick personality tracking
  const [quickPicks, setQuickPicks] = useState({
    primary: null,
    stress: null,
    supervisor: null
  });


  // Modal State
  const [modalState, setModalState] = useState({
    isOpen: false,
    activeSection: null,
    draftSelection: null
  });


  // Feedback State for buttons
  const [saveStatus, setSaveStatus] = useState({
    primary: false,
    stress: false,
    supervisor: false,
    global: false
  });


  // --- HANDLERS ---


  const handleGatekeeperToggle = (section) => {
    setGatekeepers(prev => ({
      ...prev,
      [section]: { ...prev[section], enabled: !prev[section].enabled }
    }));
  };


  const handleGatekeeperSelect = (section, value) => {
    setGatekeepers(prev => ({
      ...prev,
      [section]: { ...prev[section], value }
    }));
  };


  const isSectionActive = (section) => {
    if (section === 'primary') return true;
    return gatekeepers[section].enabled && gatekeepers[section].value !== '';
  };


  const handleBehaviorToggle = (section, behaviorId) => {
    if (!isSectionActive(section)) return;


    setSelections(prev => {
      const currentSectionData = prev[section];
      const isSelected = currentSectionData.includes(behaviorId);
     
      if (isSelected) {
        return { ...prev, [section]: currentSectionData.filter(id => id !== behaviorId) };
      } else {
        if (currentSectionData.length >= 4) return prev; // Max 4 restriction
        return { ...prev, [section]: [...currentSectionData, behaviorId] };
      }
    });
  };


  const handleQuickSelectSave = () => {
    if (!modalState.draftSelection) {
      alert("Please select a personality first.");
      return;
    }
   
    setQuickPicks(prev => ({
      ...prev,
      [modalState.activeSection]: modalState.draftSelection
    }));


    setModalState({ isOpen: false, activeSection: null, draftSelection: null });
  };


  const applySection = (section) => {
    const hasFourBehaviors = selections[section].length === 4;
    const hasQuickPick = quickPicks[section] !== null;


    if (!hasFourBehaviors && !hasQuickPick) {
      alert(`Please select exactly 4 behaviors OR quick-pick a personality for this section before applying.`);
      return;
    }


    setSaveStatus(prev => ({ ...prev, [section]: true }));
    setTimeout(() => {
      setSaveStatus(prev => ({ ...prev, [section]: false }));
    }, 3000);
  };


  const applyGlobalFitment = () => {
    if (selections.primary.length < 4 && !quickPicks.primary) {
      alert("Please select exactly 4 behaviors or pick a personality for the Primary Role before applying.");
      return;
    }


    if (isSectionActive('stress') && selections.stress.length < 4 && !quickPicks.stress) {
      alert("Please fulfill the Requirement Under Stress, or turn the section switch off.");
      return;
    }


    if (isSectionActive('supervisor') && selections.supervisor.length < 4 && !quickPicks.supervisor) {
      alert("Please fulfill the Supervisor Personality, or turn the section switch off.");
      return;
    }


    setSaveStatus(prev => ({ ...prev, global: true }));
    setTimeout(() => {
      setSaveStatus(prev => ({ ...prev, global: false }));
    }, 3000);
  };


  // --- RENDER HELPERS ---


  const renderBehaviorGrid = (section) => {
    const active = isSectionActive(section);
    const selectedCount = selections[section].length;


    return (
      <div className={`behavior-container ${!active ? 'section-disabled' : ''}`}>
        {Object.entries(BEHAVIORS).map(([traitKey, traitBehaviors]) => (
          <div key={traitKey} className="trait-column">
            <div className="trait-header-col">
              <div className="trait-dot" style={{ backgroundColor: TRAIT_CONFIG[traitKey].color }}></div>
              {TRAIT_CONFIG[traitKey].name}
            </div>
            {traitBehaviors.map(b => {
              const isChecked = selections[section].includes(b.id);
              const isDisabled = !active || (!isChecked && selectedCount >= 4);
              const activeClass = isChecked ? 'active' : '';


              return (
                <label key={b.id} className={`behavior-item item-${traitKey} ${activeClass}`}>
                  <span className="behavior-label">{b.label}</span>
                  <div className="custom-switch-wrap">
                    <input
                      type="checkbox"
                      className="switch-input"
                      checked={isChecked}
                      onChange={() => handleBehaviorToggle(section, b.id)}
                      disabled={isDisabled}
                    />
                    <span className="switch-slider"></span>
                  </div>
                </label>
              );
            })}
          </div>
        ))}
      </div>
    );
  };


  const renderSection = (section, title) => {
    const isGatekept = section !== 'primary';
    const active = isSectionActive(section);
    const checkedCount = selections[section].length;
    const isFull = checkedCount >= 4;
    const pickedPersonalityObj = PERSONALITIES.find(p => p.id === quickPicks[section]);


    return (
      <div className="card-std" id={`sec-${section}`}>
        <h3 className="section-main-title">{title}</h3>
       
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-3">
         
          {/* Left: Tracker */}
          <div className="d-flex align-items-center gap-2">
            <span className="fw-medium text-dark" style={{ fontSize: 'var(--font-size-sm)' }}>Please Select 4 behaviors</span>
            <span className={`badge-counter ${isFull ? 'full' : ''}`}>{checkedCount}/4</span>
          </div>


          {/* Center: Gatekeeper Pill */}
          {isGatekept && (
            <div className="section-controls-pill mx-auto">
              <label className="custom-switch-wrap mb-0">
                <input
                  type="checkbox"
                  className="switch-input"
                  checked={gatekeepers[section].enabled}
                  onChange={() => handleGatekeeperToggle(section)}
                />
                <span className="switch-slider"></span>
              </label>
              <span className="fw-bold text-dark" style={{ fontSize: 'var(--font-size-sm)' }}>
                {section === 'stress' ? 'Stress Frequency' : 'Supervisor Importance'}
              </span>
              <select
                className="form-select"
                value={gatekeepers[section].value}
                onChange={(e) => handleGatekeeperSelect(section, e.target.value)}
              >
                <option value="">Select</option>
                {section === 'stress' ? (
                  <>
                    <option value="rarely">Rarely</option>
                    <option value="sometimes">Sometimes</option>
                    <option value="often">Often</option>
                    <option value="very_often">Very Often</option>
                  </>
                ) : (
                  <>
                    <option value="very_much">Very Much</option>
                    <option value="average">Average</option>
                    <option value="not_really">Not Really</option>
                    <option value="insignificant">Insignificant</option>
                  </>
                )}
              </select>
            </div>
          )}
          {!isGatekept && <div className="mx-auto"></div>}


          {/* Right: Actions */}
          <div className="section-actions d-flex gap-2 ms-auto align-items-center">
            <button
              className="btn btn-outline-primary flex-shrink-0"
              disabled={!active}
              onClick={() => setModalState({ isOpen: true, activeSection: section, draftSelection: quickPicks[section] })}
            >
              {pickedPersonalityObj ? (
                <><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"></path><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path></svg> Change Personality</>
              ) : (
                <><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg> Quick Select Personality</>
              )}
            </button>
            <button className="btn btn-outline-secondary flex-shrink-0" disabled={!active}>
              Check Personalities
            </button>
            <button
              className="btn btn-primary"
              disabled={!active}
              onClick={() => applySection(section)}
              style={saveStatus[section] ? { backgroundColor: 'var(--color-success-text)', borderColor: 'var(--color-success-text)' } : {}}
            >
              {saveStatus[section] ? "Saved ✓" : "Apply"}
            </button>
          </div>
        </div>


        {/* Quick Pick Banner Confirmation */}
        {pickedPersonalityObj && active && (
          <div className="qs-selection-banner">
            <span className="fw-bold" style={{ color: 'var(--color-success-text)', marginRight: '6px' }}>✓ Personality Picked:</span>
            <strong>{pickedPersonalityObj.name}</strong> - {pickedPersonalityObj.desc}
          </div>
        )}


        {/* Behavior Grid Rendering */}
        {renderBehaviorGrid(section)}


      </div>
    );
  };


  return (
    <>
      <style dangerouslySetInnerHTML={{__html: `
        @charset "UTF-8";
        @import url("https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css");
        @import url("https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap");


        :root {
          /* Raw Colors */
          --color-blue-500: #3B7CF2; --color-indigo-100: #eef2ff;
          --color-green-100: #d1e7dd; --color-green-700: #198754;
          --color-yellow-100: #fff3cd; --color-yellow-700: #856404;
          --color-red-100: #f8d7da; --color-red-700: #dc3545;
          --color-gray-50: #F3F5F9; --color-gray-100: #e5e7eb;
          --color-gray-200: #d0d5dd; --color-gray-700: #667085;
          --color-gray-800: #344054; --color-gray-900: #1F222A;


          /* Semantic Brand */
          --brand-primary: var(--color-blue-500);
          --brand-accent: #081DA5;
          --brand-bg: var(--color-gray-50);
          --brand-text: var(--color-gray-900);
          --surface-bg: #ffffff; --surface-bg-hover: var(--color-gray-50);


          /* UI Colors */
          --color-info-bg: var(--color-indigo-100); --color-success-bg: var(--color-green-100);
          --color-success-text: var(--color-green-700); --color-warning-bg: var(--color-yellow-100);
          --color-warning-text: var(--color-yellow-700); --color-danger-bg: var(--color-red-100);
          --color-danger-text: var(--color-red-700);
          --ui-gray-bg: var(--color-gray-100); --ui-border: var(--color-gray-200);
          --ui-text-muted: var(--color-gray-700); --ui-text-dark: var(--color-gray-800);


          /* EXACT SIZING FROM MAIN.CSS */
          --type-ratio: 1.125;
          --font-size-base: 0.6875rem; /* ~11px Standard Body */
          --font-size-sm: calc(var(--font-size-base) / var(--type-ratio)); /* ~9.77px Small Text */
          --font-size-h3: clamp(1rem, 0.9rem + 0.5vw, 1.1875rem); /* ~16-19px */
          --radius-md: 0.5rem; --radius-lg: 0.75rem;
          --margin-base: 1.5rem; --padding-card: 1.5rem;
          --font-family-base: 'Inter', sans-serif;
          --line-height-body: 1.7;


          /* Palette for Traits */
          --color-influence-bg: #EDE9FE; --color-influence-text: #A78BFA;
          --color-steadiness-bg: #D1FAE5; --color-steadiness-text: #6EE7B7;
          --color-dominance-bg: #FCE7F3; --color-dominance-text: #F472B6;
          --color-consc-bg: #FEF3C7; --color-consc-text: #FCD34D;
        }


        body { margin: 0; padding: 0; font-family: var(--font-family-base) !important; font-size: var(--font-size-base); background-color: var(--brand-bg); color: var(--brand-text); -webkit-font-smoothing: antialiased; line-height: var(--line-height-body); }
        .ds-wrapper { min-height: 100vh; display: flex; flex-direction: column; }
        .ds-header { background: var(--surface-bg); border-bottom: 1px solid var(--ui-border); padding: 0.75rem 1.5rem; position: sticky; top: 0; z-index: 100; box-shadow: 0 1px 3px rgba(0,0,0,0.05); }
        .ds-header h2 { font-size: 1.25rem; font-weight: 700; margin: 0; color: var(--brand-text); }
        .ds-content { flex: 1; padding: var(--margin-base); max-width: 1400px; margin: 0 auto; width: 100%; }
       
        .card-std { background: var(--surface-bg); border: 1px solid var(--ui-border); border-radius: var(--radius-lg); padding: var(--padding-card); margin-bottom: var(--margin-base); box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02); }
        .section-main-title { font-size: var(--font-size-h3); font-weight: 700; color: var(--brand-text); margin-bottom: 1.25rem; }
       
        /* The Pill-Shaped Gatekeeper */
        .section-controls-pill { display: inline-flex; align-items: center; gap: 0.75rem; background: var(--surface-bg); padding: 4px 6px 4px 10px; border-radius: 50px; border: 1px solid var(--ui-border); white-space: nowrap; flex-shrink: 0; box-shadow: 0 1px 2px rgba(0,0,0,0.02); }
        .section-controls-pill .form-select { min-height: 28px !important; height: 28px; padding: 0 24px 0 12px !important; border-radius: 20px !important; background-color: var(--ui-gray-bg); border: 1px solid transparent !important; min-width: 120px; font-size: var(--font-size-base) !important; color: var(--ui-text-dark) !important; box-shadow: none; }
        .section-controls-pill .form-select:focus { border-color: var(--brand-primary) !important; background-color: var(--surface-bg); }


        .badge-counter { background-color: var(--brand-accent); color: white; padding: 4px 10px; border-radius: 20px; font-size: var(--font-size-sm); font-weight: 700; display: inline-block; transition: 0.3s; line-height: 1.2;}
        .badge-counter.full { background-color: var(--color-success-text); }


        /* Buttons matching main.css strictly */
        .btn { font-size: var(--font-size-base) !important; font-weight: 600; padding: 0.375rem 1rem; border-radius: var(--radius-md); transition: 0.2s; display: inline-flex; align-items: center; justify-content: center; gap: 0.5rem; line-height: 1.5; }
        .btn-primary { background-color: var(--brand-accent); border-color: var(--brand-accent); color: #fff; }
        .btn-primary:hover:not(:disabled) { background-color: var(--brand-primary); }
        .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
       
        .btn-outline-primary { background-color: var(--surface-bg); border: 1px solid var(--brand-accent); color: var(--brand-accent); }
        .btn-outline-primary:hover:not(:disabled) { background-color: var(--color-info-bg); }
        .btn-outline-primary:disabled { opacity: 0.5; cursor: not-allowed; border-color: var(--ui-border); color: var(--ui-text-muted); }
       
        .btn-outline-secondary { background-color: var(--surface-bg); border: 1px solid var(--ui-border); color: var(--ui-text-dark); }
        .btn-outline-secondary:hover:not(:disabled) { background-color: var(--ui-gray-bg); color: var(--brand-primary); }
        .btn-outline-secondary:disabled { opacity: 0.5; cursor: not-allowed; color: var(--ui-text-muted); }


        /* Exact Switch Toggle from main.css */
        .custom-switch-wrap { display: flex; align-items: center; cursor: pointer; gap: 8px; margin: 0; user-select: none; }
        .switch-input { position: absolute; opacity: 0; width: 0; height: 0; }
        .switch-slider { position: relative; display: inline-block; width: 36px; height: 20px; background-color: var(--ui-border); border-radius: 34px; transition: background-color 0.3s; flex-shrink: 0; }
        .switch-slider::before { position: absolute; content: ""; height: 16px; width: 16px; left: 2px; bottom: 2px; background-color: #fff; transition: transform 0.3s; border-radius: 50%; box-shadow: 0 1px 2px rgba(0,0,0,0.2); }
        .switch-input:checked + .switch-slider { background-color: var(--brand-accent); }
        .switch-input:checked + .switch-slider::before { transform: translateX(16px); }


        /* Behavior Grid System */
        .behavior-container { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 0.75rem; }
        @media (max-width: 992px) { .behavior-container { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 576px) { .behavior-container { grid-template-columns: 1fr; } }
       
        .trait-column { display: flex; flex-direction: column; gap: 0.5rem; }
        .trait-header-col { display: flex; align-items: center; gap: 6px; font-size: var(--font-size-sm); font-weight: 700; color: var(--ui-text-muted); padding-bottom: 2px; }
        .trait-dot { width: 10px; height: 10px; border-radius: 2px; }
       
        .behavior-item { background: var(--surface-bg-hover); border: 1px solid var(--ui-border); border-radius: 6px; padding: 6px 8px 6px 12px; display: flex; align-items: center; justify-content: space-between; transition: all 0.2s ease; position: relative; overflow: hidden; cursor: pointer; margin: 0; min-height: 36px; }
        .behavior-item::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 4px; }
        .behavior-label { font-size: var(--font-size-base); font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; padding-right: 8px; color: var(--brand-text); }
       
        .item-steadiness::before { background-color: var(--color-steadiness-text); }
        .item-influence::before { background-color: var(--color-influence-text); }
        .item-dominance::before { background-color: var(--color-dominance-text); }
        .item-consc::before { background-color: var(--color-consc-text); }
       
        .item-steadiness.active { background-color: var(--color-steadiness-bg); border-color: transparent; }
        .item-influence.active { background-color: var(--color-influence-bg); border-color: transparent; }
        .item-dominance.active { background-color: var(--color-dominance-bg); border-color: transparent; }
        .item-consc.active { background-color: var(--color-consc-bg); border-color: transparent; }


        /* Banner */
        .qs-selection-banner { background-color: var(--color-success-bg); border: 1px solid var(--color-success-text); border-radius: var(--radius-md); padding: 8px 12px; font-size: var(--font-size-base); margin-bottom: 1rem; color: var(--brand-text); }


        /* BLUR STATE LOGIC */
        .section-disabled { opacity: 0.35; filter: grayscale(0.2) blur(3px); pointer-events: none; user-select: none; transition: filter 0.4s ease, opacity 0.4s ease; }


        .action-footer { position: sticky; bottom: 0; background: var(--surface-bg); padding: 1rem 2rem; border-top: 1px solid var(--ui-border); display: flex; justify-content: flex-end; align-items: center; gap: 1rem; z-index: 100; box-shadow: 0 -4px 12px rgba(0,0,0,0.03); }


        /* Quick Select Modal Styles */
        .qs-modal-overlay { position: fixed; inset: 0; background-color: rgba(0,0,0,0.5); z-index: 1050; display: none; align-items: center; justify-content: center; backdrop-filter: blur(2px); }
        .qs-modal-overlay.active { display: flex; }
        .qs-modal-dialog { background: var(--surface-bg); width: 96%; max-width: 1400px; max-height: 90vh; border-radius: var(--radius-lg); display: flex; flex-direction: column; box-shadow: 0 10px 25px rgba(0,0,0,0.15); }
        .qs-modal-header { padding: 1rem 1.5rem; border-bottom: 1px solid var(--ui-border); display: flex; justify-content: space-between; align-items: center; }
        .qs-modal-header h3 { font-size: 1rem !important; margin: 0; font-weight: 700; color: var(--brand-text); }
        .qs-btn-close { background: none; border: none; font-size: 24px; cursor: pointer; color: var(--ui-text-muted); line-height: 1; padding: 0; display: flex; align-items: center; justify-content: center; }
        .qs-modal-body { padding: 1.5rem; overflow-y: auto; flex: 1; background: var(--brand-bg); scrollbar-width: thin; }
        .qs-modal-footer { padding: 1rem 1.5rem; border-top: 1px solid var(--ui-border); display: flex; justify-content: flex-end; background: var(--surface-bg); }
        .qs-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 1rem; }
        @media (max-width: 1200px) { .qs-grid { grid-template-columns: repeat(3, 1fr); } }
        @media (max-width: 768px) { .qs-grid { grid-template-columns: 1fr; } }
       
        .qs-card { background: var(--surface-bg); border: 1px solid var(--ui-border); border-radius: var(--radius-md); padding: 12px; cursor: pointer; transition: all 0.2s ease; display: flex; flex-direction: column; margin: 0; }
        .qs-card:hover { border-color: var(--brand-primary); }
        .qs-card:has(input[type="radio"]:checked) { border-color: var(--brand-accent); box-shadow: 0 0 0 1px var(--brand-accent); background-color: var(--color-info-bg); }
        .qs-card-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px; }
        .qs-card-title { font-weight: 700; font-size: var(--font-size-base); color: var(--brand-text); }
        .qs-card-desc { font-size: var(--font-size-sm); color: var(--ui-text-dark); line-height: 1.4; margin: 0; }
       
        .custom-radio { width: 16px; height: 16px; appearance: none; -webkit-appearance: none; border: 1px solid var(--ui-border); border-radius: 50%; outline: none; cursor: pointer; position: relative; background: var(--surface-bg); flex-shrink: 0; margin: 0; transition: all 0.2s; }
        .custom-radio:checked { border-color: var(--brand-accent); border-width: 4px; }
      `}} />


      <div className="ds-wrapper">
        <header className="ds-header">
          <h2>Personality Fitment Configuration</h2>
        </header>


        <div className="ds-content">
          {renderSection('primary', 'Primary Role Requirements')}
          {renderSection('stress', 'Requirement Under Stress')}
          {renderSection('supervisor', 'Supervisor Personality')}
        </div>


        {/* Global Action Footer */}
        <div className="action-footer">
          <button className="btn btn-outline-secondary" onClick={() => alert("Closing view...")}>Close</button>
          <button
            className="btn btn-primary px-4"
            onClick={applyGlobalFitment}
            style={saveStatus.global ? { backgroundColor: 'var(--color-success-text)', borderColor: 'var(--color-success-text)' } : {}}
          >
            {saveStatus.global ? "Saved Successfully ✓" : "Save Profile"}
          </button>
        </div>
      </div>


      {/* QUICK SELECT MODAL */}
      <div
        className={`qs-modal-overlay ${modalState.isOpen ? 'active' : ''}`}
        onClick={(e) => { if(e.target === e.currentTarget) setModalState({...modalState, isOpen: false}); }}
      >
        <div className="qs-modal-dialog">
          <div className="qs-modal-header">
            <h3>Quick Select Personality</h3>
            <button className="qs-btn-close" onClick={() => setModalState({...modalState, isOpen: false})}>×</button>
          </div>
          <div className="qs-modal-body">
            <div className="qs-grid">
              {PERSONALITIES.map(p => (
                <label key={p.id} className="qs-card">
                  <div className="qs-card-header">
                    <span className="qs-card-title">{p.name}</span>
                    <input
                      type="radio"
                      name="quick_personality"
                      className="custom-radio"
                      checked={modalState.draftSelection === p.id}
                      onChange={() => setModalState({...modalState, draftSelection: p.id})}
                    />
                  </div>
                  <p className="qs-card-desc">{p.desc}</p>
                </label>
              ))}
            </div>
          </div>
          <div className="qs-modal-footer">
            <button className="btn btn-primary px-4" onClick={handleQuickSelectSave}>Save</button>
          </div>
        </div>
      </div>
    </>
  );
}

